import { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Droplet, MailCheck, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden" style={{ background: "linear-gradient(150deg, #451a03, #78350f 55%, #b45309)" }}>
        <Link to="/" className="flex items-center gap-2 text-amber-100 relative z-10">
          <Droplet className="w-7 h-7" style={{ color: "#F59E0B" }} fill="#F59E0B" />
          <span className="font-display text-3xl font-bold">Drops of Honey</span>
        </Link>
        <div className="relative z-10 text-amber-50">
          <h2 className="font-display text-5xl font-bold leading-tight">Locked out of your collection?</h2>
          <p className="mt-4 text-amber-100/80 max-w-md">No worries — we'll send you a secure link to set a new password and get you back to your drops of honey.</p>
        </div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(245,158,11,0.35), transparent 70%)" }} />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm float-up">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <Droplet className="w-7 h-7 brand-text" fill="currentColor" />
            <span className="font-display text-2xl font-bold">Drops of Honey</span>
          </div>

          {sent ? (
            <div data-testid="forgot-sent-message">
              <div className="w-14 h-14 rounded-full grid place-items-center mb-4" style={{ background: "var(--honey-100)" }}>
                <MailCheck className="w-7 h-7 brand-text" />
              </div>
              <h1 className="font-display text-4xl font-bold">Check your email</h1>
              <p className="text-muted-page mt-2">If an account exists for <span className="font-semibold">{email}</span>, a password reset link is on its way. The link expires in 10 minutes.</p>
              <Link to="/login" data-testid="back-to-login-link" className="inline-flex items-center gap-1 brand-text font-semibold honey-underline mt-6">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="forgot-password-form">
              <h1 className="font-display text-4xl font-bold">Forgot password?</h1>
              <p className="text-muted-page mt-1 mb-6">Enter your email and we'll send you a reset link.</p>

              {error && <div data-testid="forgot-error" className="mb-4 text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-lg px-3 py-2">{error}</div>}

              <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Email</label>
              <input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full surface border rounded-lg px-3 py-2.5 mb-6 focus:outline-none focus:ring-2 focus:ring-amber-400" />

              <button data-testid="forgot-submit-btn" disabled={loading} className="w-full py-3 rounded-full font-semibold disabled:opacity-60" style={{ background: "var(--brand)", color: "#fff" }}>
                {loading ? "Sending…" : "Send reset link"}
              </button>
              <Link to="/login" data-testid="back-to-login-link" className="inline-flex items-center gap-1 text-sm text-muted-page hover:brand-text mt-4">
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
